import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Modal, Select, Table, message } from 'antd';
import { Download, Plus, Search } from 'lucide-react';
import Layout from '../../Layout';
import { hrApi, useHr } from './hrStore';
import {
  CONTRACT_KIND, CSTATE, FIXED_MAX_TIMES, branchById, contractState, currentContract, daysBetween, empById, empContracts, fmtD, fmtMoney,
  normText, positionById, toDay, type CState, type Contract,
} from './hrLogic';
import { Badge, CStateTag, Chip, EmpCell, KindTag, PageHeader, empUrl, exportXlsx, useHrPerms } from './hrUi';
import { ContractModal, EvalModal } from './hrModals';

type F = 'todo' | 'expiring' | 'expired' | 'unsigned' | 'probation' | 'current' | 'all';
const FILTERS: { key: F; label: string; tone?: 'rose' | 'amber' }[] = [
  { key: 'todo', label: 'Cần xử lý', tone: 'rose' }, { key: 'expiring', label: 'Hết hạn ≤ 30 ngày', tone: 'amber' }, { key: 'expired', label: 'Quá hạn', tone: 'rose' },
  { key: 'unsigned', label: 'Chưa ký', tone: 'rose' }, { key: 'probation', label: 'Thử việc' }, { key: 'current', label: 'Đang áp dụng' }, { key: 'all', label: 'Tất cả' },
];

/** Hợp đồng lao động: nhắc gia hạn, quá hạn, chưa ký; áp quy tắc 2 lần HĐ xác định thời hạn */
export default function Contracts({ openNew = false }: { openNew?: boolean }) {
  const s = useHr();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { me, canEdit, canExport } = useHrPerms();
  const today = toDay();
  const f = (params.get('f') as F) || 'todo';
  const q = params.get('q') || '';
  const branchId = params.get('branch') || '';
  const [modal, setModal] = useState<{ empId?: string } | null>(() => (openNew || params.get('new') ? { empId: params.get('emp') || undefined } : null));
  const [evalFor, setEvalFor] = useState<string | null>(null);
  const go = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  const rows = useMemo(() => s.contracts.filter(c => !c.cancelled).map(c => {
    const e = empById(s, c.empId)!;
    const state = contractState(s, c, today);
    const isCurrent = currentContract(s, c.empId, today)?.id === c.id || (state === 'upcoming');
    const seq = c.kind === 'fixed' ? empContracts(s, c.empId).filter(x => x.kind === 'fixed').findIndex(x => x.id === c.id) + 1 : 0;
    return { c, e, state, isCurrent, seq, left: c.end ? daysBetween(today, c.end) : null };
  }).filter(r => r.e), [s, today]);

  const live = (r: typeof rows[number]) => r.state !== 'ended';
  const match = (r: typeof rows[number], k: F) => {
    switch (k) {
      case 'todo': return live(r) && (!!CSTATE[r.state].level || (!r.c.signedAt && r.state !== 'leaving'));
      case 'expiring': return r.state === 'expiring' || r.state === 'eval_due';
      case 'expired': return ['expired', 'auto_indefinite', 'eval_overdue'].includes(r.state);
      case 'unsigned': return live(r) && !r.c.signedAt && r.state !== 'leaving';
      case 'probation': return r.c.kind === 'probation' && live(r) && r.state !== 'leaving';
      case 'current': return r.isCurrent && live(r);
      default: return true;
    }
  };
  const scoped = rows.filter(r => (!branchId || r.e.branchId === branchId) && (!q || normText(`${r.e.name} ${r.e.code} ${r.c.code}`).includes(normText(q))));
  const list = scoped.filter(r => match(r, f)).sort((a, b) => (a.left ?? 99999) - (b.left ?? 99999) || b.c.start.localeCompare(a.c.start));

  const doExport = () => exportXlsx('hop_dong_lao_dong', 'Hợp đồng', list.map(r => ({
    'Số HĐ': r.c.code, 'Mã NV': r.e.code, 'Họ tên': r.e.name, 'Vị trí': positionById(s, r.e.positionId)?.name || '', 'Chi nhánh': branchById(s, r.e.branchId)?.name || '',
    'Loại': CONTRACT_KIND[r.c.kind].label, 'Hiệu lực': fmtD(r.c.start), 'Hết hạn': fmtD(r.c.end), 'Lương': r.c.salary, 'Ngày ký': fmtD(r.c.signedAt), 'Trạng thái': CSTATE[r.state].label,
  })));
  const sign = (c: Contract) => Modal.confirm({
    title: `Xác nhận đã ký ${c.code}?`, content: `Ngày ký ${fmtD(today)}`, okText: 'Đã ký', cancelText: 'Đóng',
    onOk: () => { const r = hrApi.signContract(c.id, today, me); if (r.ok) message.success('Đã cập nhật'); else message.error(r.errors?.join(', ')); },
  });

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Hợp đồng lao động" desc={`Nhắc trước ${30} ngày khi HĐ xác định thời hạn hết hạn · tối đa ${FIXED_MAX_TIMES} lần ký XĐTH, lần sau phải ký không xác định thời hạn`}
          extra={<>
            {canExport && <Button icon={<Download size={14} />} onClick={doExport}>Xuất Excel</Button>}
            {canEdit && <Button type="primary" icon={<Plus size={14} />} onClick={() => setModal({})}>Lập hợp đồng</Button>}
          </>} />
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm space-y-3">
          <div className="flex flex-wrap gap-2">
            <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tên, mã NV, số HĐ" className="!w-[240px]" value={q} onChange={e => go({ q: e.target.value || null })} />
            <Select className="w-[180px]" value={branchId} onChange={v => go({ branch: v || null })} options={[{ value: '', label: 'Tất cả chi nhánh' }, ...s.branches.map(b => ({ value: b.id, label: b.name }))]} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map(x => { const n = scoped.filter(r => match(r, x.key)).length; return <Chip key={x.key} active={f === x.key} tone={n && x.tone ? x.tone : 'gray'} onClick={() => go({ f: x.key === 'todo' ? null : x.key })}>{x.label} · {n}</Chip>; })}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
          <Table size="middle" rowKey={r => r.c.id} dataSource={list} scroll={{ x: 1100 }} pagination={{ pageSize: 20, showSizeChanger: false, showTotal: t => `${t} hợp đồng` }}
            columns={[
              { title: 'Số HĐ', key: 'code', width: 130, render: (_, r) => <div><div className="font-medium">{r.c.code}</div>{r.seq > 0 && <div className="text-[11px] text-gray-400">XĐTH lần {r.seq}/{FIXED_MAX_TIMES}</div>}</div> },
              { title: 'Nhân sự', key: 'emp', width: 250, render: (_, r) => <EmpCell s={s} e={r.e} /> },
              { title: 'Loại', key: 'kind', width: 120, render: (_, r) => <KindTag kind={r.c.kind} /> },
              { title: 'Thời hạn', key: 'term', width: 200, render: (_, r) => (
                <div>
                  <div>{fmtD(r.c.start)} – {r.c.end ? fmtD(r.c.end) : 'không thời hạn'}</div>
                  {r.left !== null && r.state !== 'ended' && r.state !== 'renewed' && r.state !== 'leaving' && (
                    <div className={`text-[11px] ${r.left < 0 ? 'text-rose-600' : r.left <= 30 ? 'text-amber-600' : 'text-gray-400'}`}>{r.left < 0 ? `quá hạn ${-r.left} ngày` : `còn ${r.left} ngày`}</div>
                  )}
                </div>
              ) },
              { title: 'Lương', key: 'salary', width: 130, render: (_, r) => fmtMoney(r.c.salary) },
              { title: 'Trạng thái', key: 'state', width: 190, render: (_, r) => (
                <div className="flex flex-wrap gap-1"><CStateTag state={r.state as CState} />{!r.c.signedAt && r.state !== 'ended' && <Badge className="bg-rose-50 text-rose-700 border-rose-200">Chưa ký</Badge>}</div>
              ) },
              { title: '', key: 'act', width: 190, render: (_, r) => canEdit && r.state !== 'ended' && (
                <div className="flex flex-wrap gap-1">
                  {!r.c.signedAt && <Button size="small" type="primary" onClick={() => sign(r.c)}>Đã ký</Button>}
                  {r.c.kind === 'probation' && !r.c.evaluation && ['eval_due', 'eval_overdue', 'active'].includes(r.state) && <Button size="small" onClick={() => setEvalFor(r.c.id)}>Đánh giá</Button>}
                  {['expiring', 'expired', 'auto_indefinite'].includes(r.state) && <Button size="small" type="primary" ghost onClick={() => setModal({ empId: r.e.id })}>Gia hạn</Button>}
                  <Button size="small" type="text" onClick={() => navigate(empUrl(r.e.id, 'contracts'))}>Hồ sơ</Button>
                </div>
              ) },
            ]} />
        </div>
      </div>
      {modal && <ContractModal empId={modal.empId} onClose={() => { setModal(null); if (params.get('new')) go({ new: null, emp: null }); }} />}
      {evalFor && <EvalModal contractId={evalFor} onClose={() => setEvalFor(null)} onPassed={empId => setModal({ empId })} />}
    </Layout>
  );
}
